import React, { useEffect, useMemo, useState } from 'react';
import { useSalaryData } from './data/useSalaryData';
import {
    SalaryData,
    SalarySummaryRow,
} from './types';
import { formatSalary, formatSample } from './utils/formatters';
import CompanySizeImpact from './components/CompanySizeImpact';
import AboutProject from './components/AboutProject';
import ExperienceGrowth from './components/ExperienceGrowth';
import PositionComparison from './components/PositionComparison';
import ProjectFooter from './components/ProjectFooter';
import PositionRanking from './components/PositionRanking';
import SectionHeader from './components/SectionHeader';

const SiteHeader = () => (
    <header className="site-header">
        <div className="page-shell header-inner">
            <a className="brand-link" href="/" aria-label="Ege Aksoy portföyüne dön">
                <span>EA</span>
                <span className="brand-divider" aria-hidden="true" />
                <span>Veri Notu</span>
            </a>
            <nav aria-label="Sayfa bağlantıları">
                <a href="#pozisyonlar">Pozisyonlar</a>
                <a href="#deneyim">Deneyim</a>
                <a href="#sirketler">Şirketler</a>
                <a href="#karsilastir">Karşılaştır</a>
            </nav>
            <a className="portfolio-link" href="/">
                Portföye dön <span aria-hidden="true">↗</span>
            </a>
        </div>
    </header>
);

const LoadingState = () => (
    <>
        <SiteHeader />
        <main className="loading-page page-shell" aria-busy="true" aria-live="polite">
            <p className="eyebrow">Veriler hazırlanıyor</p>
            <div className="loading-title" />
            <div className="loading-subtitle" />
            <div className="loading-metrics">
                {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="loading-card" />
                ))}
            </div>
            <span className="sr-only">Maaş verileri yükleniyor.</span>
        </main>
    </>
);

const ErrorState: React.FC<{
    message: string;
    onRetry: () => void;
}> = ({ message, onRetry }) => (
    <>
        <SiteHeader />
        <main className="error-page page-shell">
            <p className="eyebrow">Veri bağlantısı</p>
            <h1>Grafikler şu anda yüklenemiyor.</h1>
            <p>{message}</p>
            <button className="primary-button" type="button" onClick={onRetry}>
                Yeniden dene
            </button>
        </main>
    </>
);

const getMetrics = (
    rows: SalarySummaryRow[],
    data: SalaryData,
    selectedYear: number
) => {
    const yearRows = rows.filter((row) => row.year === selectedYear);
    const highest = [...yearRows].sort(
        (a, b) => b.medianSalary - a.medianSalary
    )[0];
    const widestSample = [...yearRows].sort(
        (a, b) => b.sampleCount - a.sampleCount
    )[0];
    const yearRange =
        data.years.length === 1
            ? `${data.years[0]}`
            : `${Math.min(...data.years)}–${Math.max(...data.years)}`;

    return [
        {
            label: 'Analiz edilen pozisyon',
            value: `${yearRows.length}`,
            detail: `${selectedYear} pozisyon özeti`,
        },
        {
            label: 'En yüksek medyan',
            value: highest?.position ?? '—',
            detail: highest ? formatSalary(highest.medianSalary) : '—',
        },
        {
            label: 'Kapsanan yıllar',
            value: yearRange,
            detail: `${data.years.length} yıl`,
        },
        {
            label: 'En geniş pozisyon örneklemi',
            value: widestSample?.position ?? '—',
            detail: widestSample
                ? formatSample(widestSample.sampleCount)
                : '—',
        },
    ];
};

const SalaryPage: React.FC<{ data: SalaryData }> = ({ data }) => {
    const [selectedYear, setSelectedYear] = useState(data.years[0]);

    useEffect(() => {
        if (!data.years.includes(selectedYear)) {
            setSelectedYear(data.years[0]);
        }
    }, [data.years, selectedYear]);

    const metrics = useMemo(
        () =>
            getMetrics(data.salarySummary, data, selectedYear),
        [data, selectedYear]
    );

    return (
        <>
            <SiteHeader />
            <main>
                <section className="hero page-shell" aria-labelledby="page-title">
                    <div className="hero-grid">
                        <div className="hero-copy">
                            <p className="eyebrow">
                                Türkiye · Topluluk verisi · {selectedYear}
                            </p>
                            <h1 id="page-title">
                                Türkiye
                                <br />
                                Yazılımcı Maaşları
                            </h1>
                            <p className="hero-subtitle">
                                5.002 anonim maaş yanıtının detaylı analizi.
                                Pozisyon, deneyim seviyesi ve şirket
                                büyüklüğüne göre medyan yazılım maaşlarını
                                karşılaştırın.
                            </p>
                            <p className="hero-note">
                                <span aria-hidden="true">●</span>
                                Veriler medyan maaş hesaplamalarına dayanmaktadır.
                            </p>
                        </div>
                        <aside
                            className="hero-stamp"
                            aria-label={`${selectedYear} yılı maaş verisi`}
                        >
                            <span>TR</span>
                            <strong>{selectedYear}</strong>
                            <p>
                                Yazılım
                                <br />
                                maaş görünümü
                            </p>
                        </aside>
                    </div>

                    <div className="metric-grid" aria-label="Veri özeti">
                        {metrics.map((metric, index) => (
                            <article className="metric-card" key={metric.label}>
                                <div className="metric-topline">
                                    <span>0{index + 1}</span>
                                    <span aria-hidden="true">↗</span>
                                </div>
                                <p>{metric.label}</p>
                                <strong>{metric.value}</strong>
                                <small>{metric.detail}</small>
                            </article>
                        ))}
                    </div>

                    <nav className="story-nav" aria-label="Analiz bölümleri">
                        <span>Analizi keşfet</span>
                        <a href="#pozisyonlar">01 Pozisyonlar</a>
                        <a href="#deneyim">02 Deneyim</a>
                        <a href="#sirketler">03 Şirketler</a>
                        <a href="#karsilastir">04 Karşılaştır</a>
                        <a href="#verinin-izledigi-yol">
                            05 Verinin izlediği yol
                        </a>
                    </nav>
                </section>

                <section
                    className="data-section page-shell"
                    id="pozisyonlar"
                    aria-label="Pozisyon maaş sıralaması"
                >
                    <SectionHeader
                        index="01"
                        eyebrow="Genel görünüm"
                        title="Pozisyon Maaş Sıralaması"
                        description="Pozisyonları medyan maaşa göre sırala, ara ve örneklem büyüklükleriyle birlikte incele."
                        year={selectedYear}
                    />
                    <PositionRanking
                        rows={data.salarySummary}
                        years={data.years}
                        selectedYear={selectedYear}
                        onYearChange={setSelectedYear}
                    />
                </section>

                <section
                    className="data-section page-shell"
                    id="deneyim"
                    aria-label="Deneyime göre maaş gelişimi"
                >
                    <SectionHeader
                        index="02"
                        eyebrow="Kariyer eğrisi"
                        title="Deneyime Göre Maaş Gelişimi"
                        description="Bir pozisyon seçerek deneyim basamaklarında medyan maaşın nasıl değiştiğini gör."
                        year={selectedYear}
                    />
                    <ExperienceGrowth
                        rows={data.positionExperience}
                        selectedYear={selectedYear}
                    />
                </section>

                <section
                    className="data-section page-shell"
                    id="sirketler"
                    aria-label="Şirket büyüklüğünün maaşa etkisi"
                >
                    <SectionHeader
                        index="03"
                        eyebrow="Organizasyon ölçeği"
                        title="Şirket Büyüklüğünün Maaşa Etkisi"
                        description="Kıdem seviyesini değiştirerek şirket ölçeği ile medyan maaş arasındaki ilişkiyi incele."
                        year={selectedYear}
                    />
                    <CompanySizeImpact
                        rows={data.companySizeSeniority}
                        selectedYear={selectedYear}
                    />
                </section>

                <section
                    className="data-section page-shell"
                    id="karsilastir"
                    aria-label="Pozisyon karşılaştırması"
                >
                    <SectionHeader
                        index="04"
                        eyebrow="Kariyer yolları"
                        title="Pozisyon Karşılaştırması"
                        description="İki pozisyon seç; deneyim aralıklarındaki maaş eğrilerini aynı ölçekte karşılaştır."
                        year={selectedYear}
                    />
                    <PositionComparison
                        rows={data.positionExperience}
                        selectedYear={selectedYear}
                    />
                </section>

                <section
                    className="methodology page-shell"
                    id="metodoloji"
                    aria-labelledby="methodology-title"
                >
                    <div className="methodology-intro">
                        <p className="eyebrow">05 · Metodoloji</p>
                        <h2 id="methodology-title">Veriyi doğru okumak</h2>
                        <p>
                            Bu sayfa piyasa için kesin bir ücret tarifesi değil,
                            topluluk yanıtlarından üretilmiş açıklayıcı bir
                            görünüm sunar.
                        </p>
                    </div>
                    <div className="methodology-grid">
                        <article>
                            <span>01</span>
                            <h3>Neden medyan?</h3>
                            <p>
                                Medyan, uç maaş değerlerinin sonucu ortalamaya
                                kıyasla daha az saptırmasını sağlar ve tipik
                                yanıtı daha dengeli temsil eder.
                            </p>
                        </article>
                        <article>
                            <span>02</span>
                            <h3>Topluluk verisi</h3>
                            <p>
                                Veriler topluluk anketi yanıtlarından gelir.
                                Sayfada yalnızca işlenmiş ve toplulaştırılmış
                                CSV özetleri kullanılır.
                            </p>
                        </article>
                        <article>
                            <span>03</span>
                            <h3>Toplulaştırılmış sonuçlar</h3>
                            <p>
                                Yanıtlar pozisyon, deneyim, şirket büyüklüğü ve
                                kıdem kırılımlarında gruplanır; tekil yanıtlar
                                gösterilmez.
                            </p>
                        </article>
                        <article>
                            <span>04</span>
                            <h3>Örneklem ve güven</h3>
                            <p>
                                Daha küçük örneklemler daha oynak olabilir.
                                Katılımcı sayısı her araç ipucunda bu nedenle
                                görünür tutulur.
                            </p>
                        </article>
                    </div>
                    <div className="methodology-note">
                        <strong>Veri temizliği notu</strong>
                        <p>
                            Pozisyon adlarındaki baştaki ve sondaki boşluklar
                            normalize edilir. Aynı yıl ve kırılım yinelenirse
                            daha geniş örnekleme sahip işlenmiş özet kullanılır.
                            Farklı tablolardaki örneklem sayıları toplam
                            katılımcı sayısı gibi birleştirilmez.
                        </p>
                    </div>
                </section>
                <AboutProject />
            </main>
            <ProjectFooter />
        </>
    );
};

const App = () => {
    const { state, retry } = useSalaryData();

    if (state.status === 'loading') {
        return <LoadingState />;
    }

    if (state.status === 'error') {
        return <ErrorState message={state.message} onRetry={retry} />;
    }

    return <SalaryPage data={state.data} />;
};

export default App;
